import React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Paper,
  Stack,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Container,
  useTheme,
  useMediaQuery
} from "@mui/material";
import { motion } from "framer-motion";
import LandscapeIcon from "@mui/icons-material/Landscape";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import TempleHinduIcon from "@mui/icons-material/TempleHindu";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import ParkIcon from "@mui/icons-material/Park";
import TerrainIcon from "@mui/icons-material/Terrain";
import { useNavigate } from "react-router-dom";
import TourCard from "../Components/Cards/TourCard";

const JammuKashmirPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const tourPackages = [
    {
      id: "jk1",
      title: "Kashmir Valley Classic Tour",
      places: "Srinagar • Gulmarg • Pahalgam • Sonmarg",
      price: "₹ 14,999/-",
      image:
        "https://travelsolution.dukekashmirtravels.com/User_Files/image/blog/kashmir%20dos%20and%20dont.jpg",
      rating: 4.8,
    },
    {
      id: "jk2",
      title: "Gulmarg Snow & Ski Experience",
      places: "Gulmarg • Tangmarg • Kongdoori",
      price: "₹ 18,499/-",
      image:
        "https://www.holidify.com/images/bgImages/GULMARG.jpg",
      rating: 4.7,
    },
    {
      id: "jk3",
      title: "Spiritual Jammu & Vaishno Devi",
      places: "Jammu • Katra • Vaishno Devi",
      price: "₹ 8,999/-",
      image:
        "https://www.alkofholidays.com/storage/image-gallery/maa-vaishno-devi-temple-jammu.jpg",
      rating: 4.6,
    },
    {
      id: "jk4",
      title: "Kashmir Honeymoon Special",
      places: "Srinagar • Houseboat • Pahalgam • Gulmarg",
      price: "₹ 19,999/-",
      image:
        "https://www.bharatbooking.com/admin/webroot/img/uploads/holiday-package/1701150890_728646-5n-6d-kashmir-luxury-honeymoon-package-image.webp",
      rating: 4.9,
    },
  ];

  const enrichTour = (tour, index) => {
    const durations = ["5D / 4N", "6D / 5N", "7D / 6N"];
    const tags = ["Group Tour", "Bestseller", "Limited Offer"];

    return {
      ...tour,
      duration: durations[index % durations.length],
      tag: tags[index % tags.length],
      oldPrice:
        "₹ " +
        (parseInt(tour.price.replace(/[₹,/-]/g, "")) + 4000).toLocaleString() +
        "/-",
      discountEndsIn: "1d 3h 42m",
    };
  };

  return (
    <Box sx={{ backgroundColor: "#F9FAFB", minHeight: "100vh" }}>
      
      {/* HERO SECTION */}
      <Box
        sx={{
          position: "relative",
          height: { xs: "60vh", md: "75vh" },
          backgroundImage:
            "url(https://images.unsplash.com/photo-1605649487212-47bdab064df7?q=80&w=1920&auto=format&fit=crop)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.7))",
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2, textAlign: "center", color: "white" }}>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h2"
              sx={{
                fontFamily: '"Playfair Display", serif',
                fontWeight: 800,
                fontSize: { xs: "2.5rem", md: "4.5rem" },
                mb: 2,
                textShadow: "0 4px 12px rgba(0,0,0,0.5)",
              }}
            >
              Jammu & Kashmir
            </Typography>
            <Typography
              variant="h5"
              sx={{ fontWeight: 300, letterSpacing: 2, mb: 4, opacity: 0.9 }}
            >
              PARADISE ON EARTH
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => document.getElementById("packages").scrollIntoView({ behavior: "smooth" })}
              sx={{
                backgroundColor: "#D97706",
                color: "white",
                px: 4,
                py: 1.5,
                borderRadius: 50,
                fontWeight: 600,
                fontSize: "1rem",
                boxShadow: "0 4px 14px rgba(217, 119, 6, 0.4)",
                "&:hover": { backgroundColor: "#B45309", transform: "translateY(-2px)" },
              }}
            >
              Explore Packages
            </Button>
          </motion.div>
        </Container>
      </Box>

      {/* INTRO SECTION */}
      <Container maxWidth="md" sx={{ py: 8, textAlign: "center" }}>
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <Typography variant="overline" sx={{ color: "#059669", fontWeight: 700, letterSpacing: 1.5 }}>
            THE JEWEL OF INDIA
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              mt: 1,
              mb: 3,
              color: "#1F2937"
            }}
          >
            Experience Heaven on Earth
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "#6B7280", lineHeight: 1.8, fontSize: "1.1rem" }}
          >
            Jammu & Kashmir is often described as <b>“Paradise on Earth.”</b> Snow-covered mountains, lush green valleys,
            crystal-clear rivers, alpine meadows, and timeless culture come together to create one of the most beautiful
            travel destinations in India. Whether you seek the calm of Dal Lake or the thrill of Gulmarg, memories await.
          </Typography>
        </motion.div>
      </Container>

      {/* PACKAGES SECTION */}
      <Box id="packages" sx={{ py: 6, backgroundColor: "#fff" }}>
        <Container maxWidth="xl">
          <Typography
            variant="h3"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              mb: 5,
              textAlign: "center",
              color: "#111827"
            }}
          >
            Curated Tour Packages
          </Typography>
          
          <Grid container spacing={4}>
            {tourPackages.map((tour, index) => (
              <Grid item xs={12} sm={6} md={3} key={tour.id}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -10 }}
                >
                  <TourCard
                    tour={tour}
                    index={index}
                    onViewDetails={() =>
                      navigate("/tourdetails", {
                        state: enrichTour(tour, index),
                      })
                    }
                    onEnquire={() => navigate("/booking")}
                  />
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* PLACES SECTION (ZIG-ZAG LAYOUT) */}
      <Box sx={{ py: 10, backgroundColor: "#F9FAFB" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              mb: 8,
              textAlign: "center",
              color: "#111827"
            }}
          >
            Places You Will Explore
          </Typography>

          {[
            {
              title: "Srinagar",
              img: "https://www.stayvista.com/blog/wp-content/uploads/2024/08/Srinagar_Dal_Lake_Sudhakar_Bichali.jpg",
              desc: "Srinagar is the heart of Kashmir, famous for its beautiful Dal Lake, traditional houseboats, Mughal gardens, and old city charm. A shikara ride during sunset is an experience you will never forget.",
            },
            {
              title: "Gulmarg",
              img: "https://brownchinarkashmir.com/wp-content/uploads/2025/05/Is-it-safe-to-visit-Gulmarg_brown_chinar_kashmir.webp",
              desc: "Gulmarg is known for its snow-covered landscapes and is one of the best skiing destinations in Asia. During summer, it turns into a lush green meadow with breathtaking views of the Himalayas.",
            },
            {
              title: "Pahalgam",
              img: "https://s7ap1.scene7.com/is/image/incredibleindia/3-amarnath-yatra-pahalgam-jammu-and-kashmir-city-hero-new?qlt=82&ts=1726728787453",
              desc: "Pahalgam is a peaceful valley surrounded by pine forests and rivers. It serves as the base for the Amarnath Yatra and is ideal for nature walks, photography, and relaxation.",
            },
          ].map((place, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: i % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Grid
                container
                spacing={6}
                alignItems="center"
                sx={{
                  mb: 10,
                  flexDirection: { xs: "column", md: i % 2 === 0 ? "row" : "row-reverse" },
                }}
              >
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      borderRadius: 4,
                      overflow: "hidden",
                      boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                      height: { xs: "300px", md: "400px" },
                    }}
                  >
                    <CardMedia component="img" image={place.img} alt={place.title} sx={{ height: "100%", objectFit: "cover" }} />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="overline"
                    sx={{ color: "#059669", fontWeight: "bold" }}
                  >
                    DESTINATION
                  </Typography>
                  <Typography variant="h3" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, my: 2 }}>
                    {place.title}
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#4B5563", lineHeight: 1.8 }}>
                    {place.desc}
                  </Typography>
                </Grid>
              </Grid>
            </motion.div>
          ))}
        </Container>
      </Box>

      {/* EXPERIENCES SECTION */}
      <Box sx={{ py: 10, backgroundColor: "#fff" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              mb: 2,
              textAlign: "center",
              color: "#111827"
            }}
          >
            Experiences That Define Kashmir
          </Typography>
          <Typography sx={{ textAlign: "center", color: "#6B7280", mb: 6 }}>
            Immerse yourself in the culture and nature of the valley.
          </Typography>

          <Grid container spacing={3}>
            {[
              { icon: <DirectionsBoatIcon />, text: "Shikara rides & houseboat stays on Dal Lake" },
              { icon: <AcUnitIcon />, text: "Snowfall, skiing & winter sports in Gulmarg" },
              { icon: <LandscapeIcon />, text: "Meadows, valleys & scenic mountain views" },
              { icon: <TempleHinduIcon />, text: "Spiritual journeys to Vaishno Devi & temples" },
              { icon: <ParkIcon />, text: "Gardens, orchards & local Kashmiri culture" },
              { icon: <TerrainIcon />, text: "Hiking, pony rides & nature exploration" },
            ].map((item, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <motion.div
                  whileHover={{ y: -8 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      height: "100%",
                      borderRadius: 3,
                      border: "1px solid #E5E7EB",
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      transition: "0.3s",
                      "&:hover": {
                        borderColor: "#059669",
                        boxShadow: "0 10px 30px -10px rgba(6, 78, 59, 0.15)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: 40,
                        color: "#059669",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 60,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography fontWeight={600} sx={{ color: "#374151" }}>
                      {item.text}
                    </Typography>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* TABLE SECTION */}
      <Box sx={{ py: 10, backgroundColor: "#F3F4F6" }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: 4,
              overflow: "hidden",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Box sx={{ p: 4, borderBottom: "1px solid #E5E7EB" }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: "#111827" }}>
                Jammu & Kashmir – Travel Overview
              </Typography>
            </Box>
            <Table>
              <TableHead sx={{ backgroundColor: "#064E3B" }}>
                <TableRow>
                  <TableCell sx={{ color: "white", fontWeight: 600 }}>Place</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 600 }}>Altitude</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 600 }}>Region</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 600 }}>Known For</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  ["Srinagar", "1,585 m", "Kashmir Valley", "Dal Lake, Houseboats"],
                  ["Gulmarg", "2,650 m", "Baramulla", "Skiing & Snowfall"],
                  ["Pahalgam", "2,740 m", "Anantnag", "Valleys & Amarnath Yatra"],
                  ["Sonmarg", "2,800 m", "Ganderbal", "Glaciers & Meadows"],
                  ["Jammu", "327 m", "Jammu Region", "Vaishno Devi Temple"],
                ].map((row, i) => (
                  <TableRow
                    key={i}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 }, "&:hover": { backgroundColor: "#F9FAFB" } }}
                  >
                    {row.map((cell, j) => (
                      <TableCell key={j} sx={{ color: "#4B5563" }}>
                        {cell}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Container>
      </Box>

      {/* CTA SECTION */}
      <Box
        sx={{
          py: 12,
          backgroundImage: "linear-gradient(135deg, #065F46 0%, #047857 100%)",
          color: "white",
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h3"
              sx={{
                fontFamily: '"Playfair Display", serif',
                fontWeight: 800,
                mb: 3,
                fontSize: { xs: "2rem", md: "3rem" }
              }}
            >
              Ready to Experience Kashmir?
            </Typography>
            <Typography sx={{ maxWidth: "800px", mx: "auto", lineHeight: 1.8, fontSize: "1.1rem", opacity: 0.9, mb: 5 }}>
              From snow-covered mountains to peaceful lakes and sacred shrines, Jammu & Kashmir offers beauty, emotion, and
              memories that last a lifetime. Let us help you plan a safe, comfortable, and unforgettable journey.
            </Typography>
            <Button
              size="large"
              variant="contained"
              sx={{
                backgroundColor: "#D97706",
                color: "white",
                fontWeight: 700,
                px: 6,
                py: 1.5,
                fontSize: "1.1rem",
                borderRadius: 50,
                boxShadow: "0 4px 14px rgba(0,0,0,0.2)",
                "&:hover": { backgroundColor: "#B45309" },
              }}
              onClick={() => navigate("/booking")}
            >
              Enquire Now
            </Button>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default JammuKashmirPage;